import { useEffect } from 'react';
import HeaderFooterLayout from '../components/header-footer-layout';
import Header from '../components/header';
import StepProgress from '../components/step-progress';
import LoadingFallback from '../components/loading-fallback';
import { scrollToTop } from '../utils/helpers';
import { createEmptyBooking } from '../models/repair-bookings';
import EndUserBookingNotice from '../pages/end-user-booking-notice';
import Completion from '../pages/completion';
import ProductSelection from '../pages/product-selection';
import PurposeSelection from '../pages/purpose-selection';
import ShopSelection from '../pages/shop-selection';
import DateSelection from '../pages/date-selection';
import TimeSelection from '../pages/time-selection';
import PersonalInformationEntry from '../pages/personal-information-entry';
import FinalConfirmation from '../pages/final-confirmation';
import InvalidNavigation from '../pages/invalid-navigation';
import useBookingState from '../hooks/use-booking-state';
import useBookingCreatePages from '../hooks/use-booking-create-pages';
import { createBooking, fetchTimeSlots } from '../utils/api';
import useServiceAvailability from '../hooks/use-service-availability.ts';

const BookingCreateFlow = () => {
    const { booking, actions } = useBookingState(createEmptyBooking);
    const { available, messages } = useServiceAvailability();

    const csrfToken = window.preloadedState.environment.csrfToken;
    const {
        currentPage,
        pageConfig,
        pushPageStack,
        popPageStack,
        complete,
        clearPageStack,
        steps,
        session,
    } = useBookingCreatePages('web', csrfToken);

    useEffect(() => {
        scrollToTop();
    }, [currentPage]);

    useEffect(() => {
        if (session?.designatedShop) {
            actions.setShop(session?.designatedShop);
        }
    }, [actions, session?.designatedShop]);

    const resetFlow = () => {
        actions.reset();
        clearPageStack();
    };

    return (
        <HeaderFooterLayout
            title={currentPage.status === 'complete' ? '完了' : '来店予約'}
            onHome={resetFlow}
        >
            {pageConfig.isShopSelected && <Header title={booking.shop?.name ?? ''} />}
            {pageConfig.stepIndex !== null && (
                <StepProgress steps={steps} currentStep={pageConfig.stepIndex} />
            )}
            {currentPage.status === 'invalid' ? (
                <InvalidNavigation onClose={resetFlow} />
            ) : currentPage.status === 'precaution' ? (
                <EndUserBookingNotice
                    available={available || false}
                    messages={messages}
                    onProceed={() => {
                        actions.reset();
                        if (session?.designatedShop) {
                            actions.setShop(session?.designatedShop);
                        }
                        pushPageStack({
                            status: 'product-selection',
                            groupKey: 'root',
                        });
                    }}
                />
            ) : !session ? (
                <LoadingFallback />
            ) : currentPage.status === 'product-selection' ? (
                <ProductSelection
                    session={session}
                    groupKey={currentPage.groupKey}
                    onNext={(groupKey) => {
                        pushPageStack({ status: 'product-selection', groupKey });
                    }}
                    onComplete={(product) => {
                        actions.setProduct(product);
                        pushPageStack({ status: 'purpose-selection' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'purpose-selection' ? (
                <PurposeSelection
                    session={session}
                    productId={booking.product!.id}
                    selectedServices={booking.repairOptions}
                    toggleService={actions.toggleRepairOption}
                    actionTitle={session?.designatedShop ? '日時を選択する' : '店舗を選択する'}
                    onComplete={() => {
                        pushPageStack(
                            session?.designatedShop
                                ? { status: 'date-selection' }
                                : { status: 'shop-selection' }
                        );
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'shop-selection' ? (
                <ShopSelection
                    session={session}
                    productId={booking.product!.id}
                    selectedShop={booking.shop}
                    onComplete={(selectedShop) => {
                        actions.setShop(selectedShop);
                        pushPageStack({ status: 'date-selection' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'date-selection' ? (
                <DateSelection
                    selectedSlot={booking.selectedSlot}
                    fetchTimeSlots={() =>
                        fetchTimeSlots(
                            session.csrfToken,
                            booking.shop!.shop_id,
                            session.sessionId,
                            undefined,
                            booking.product!.id,
                            booking.repairOptions.map((s) => s.id)
                        )
                    }
                    onComplete={(date, slots) => {
                        pushPageStack({
                            status: 'time-selection',
                            selectedDate: date,
                            slots: slots,
                        });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'time-selection' ? (
                <TimeSelection
                    selectedDate={currentPage.selectedDate}
                    slots={currentPage.slots}
                    onComplete={(slot) => {
                        actions.setSelectedSlot(slot);
                        pushPageStack({ status: 'personal-info' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'personal-info' ? (
                <PersonalInformationEntry
                    info={booking.personalInfo}
                    personalInfoConfig={{ granularity: 'standard' }}
                    handleChange={actions.changePersonalInfo}
                    onComplete={() => {
                        pushPageStack({ status: 'confirm' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'confirm' ? (
                <FinalConfirmation
                    personalInfoConfig={{ granularity: 'standard' }}
                    booking={booking}
                    onSubmit={async () => {
                        const result = await createBooking(
                            session.csrfToken,
                            session.sessionId,
                            booking
                        );
                        complete(result.confirmation_id);
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'complete' ? (
                <Completion
                    type="new"
                    confirmationId={currentPage.confirmationId}
                    onClose={resetFlow}
                />
            ) : null}
        </HeaderFooterLayout>
    );
};

export default BookingCreateFlow;
